/**
 * The browser session (spec 001): the localStorage keys, what a sign-out
 * resets, and the one place a 401 ends the session.
 *
 * Kept free of imports so the API client, the stores and AuthContext can all
 * depend on it without a cycle: stores register their reset here when they
 * load, AuthContext registers the handler that navigates to the sign-in page.
 */

export const SESSION_KEYS = ['authToken', 'userEmail', 'userId'] as const;

export interface StoredSession {
  token: string;
  email: string;
  id: number;
}

const resets = new Set<() => void>();
let signOutHandler: (() => void) | null = null;
let ended = false;

/** The stored token, or undefined when signed out. */
export function storedToken(): string | undefined {
  try {
    return localStorage.getItem('authToken') || undefined;
  } catch {
    return undefined;
  }
}

export function readStoredSession(): StoredSession | null {
  const token = storedToken();
  if (!token) return null;
  const id = Number(localStorage.getItem('userId'));
  return { token, email: localStorage.getItem('userEmail') || '', id: Number.isFinite(id) ? id : 0 };
}

/** Persist a session the server just confirmed, and re-arm the 401 handling. */
export function storeSession(session: StoredSession): void {
  localStorage.setItem('authToken', session.token);
  localStorage.setItem('userEmail', session.email);
  localStorage.setItem('userId', String(session.id));
  ended = false;
}

/** Register a reset run on every sign-out (stores, caches). Returns an unregister. */
export function registerSessionReset(reset: () => void): () => void {
  resets.add(reset);
  return () => resets.delete(reset);
}

/** AuthContext's reaction to a forced sign-out (state + navigation). */
export function setSignOutHandler(handler: (() => void) | null): void {
  signOutHandler = handler;
}

/** Remove the keys and reset every registered store. */
export function clearSession(): void {
  for (const key of SESSION_KEYS) localStorage.removeItem(key);
  resets.forEach((reset) => {
    try {
      reset();
    } catch (err) {
      console.error('[session] reset failed:', err);
    }
  });
}

/**
 * An authenticated request sent with `tokenUsed` got 401. Ends the session
 * once: later 401s (concurrent requests, or stale requests from a session
 * that has since been replaced) are ignored.
 */
export function notifyUnauthorized(tokenUsed: string | undefined): void {
  if (!tokenUsed || ended) return;
  if (storedToken() !== tokenUsed) return;
  ended = true;
  clearSession();
  signOutHandler?.();
}
